import React, { useState } from 'react';
import { useAuth } from '@/AuthProvider';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LogOut, Briefcase, Users, BookOpen, Activity, Clock, BarChart2, FileText, SquarePen as PenSquare, ChevronRight, Menu, X } from 'lucide-react';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  href: string;
  children?: { label: string; href: string }[];
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Offres',
    icon: <Briefcase className="w-4 h-4" />,
    href: '/admin/offres',
  },
  {
    label: 'Parcours',
    icon: <BarChart2 className="w-4 h-4" />,
    href: '/admin/parcours',
  },
  {
    label: 'Utilisateurs',
    icon: <Users className="w-4 h-4" />,
    href: '/admin/users',
  },
  {
    label: 'Activité candidats',
    icon: <Activity className="w-4 h-4" />,
    href: '/admin/candidats',
  },
  {
    label: 'Timeline recrutement',
    icon: <Clock className="w-4 h-4" />,
    href: '/admin/timeline-recrutement',
  },
  {
    label: 'Timeline campagnes',
    icon: <BarChart2 className="w-4 h-4" />,
    href: '/admin/timeline-campagnes',
  },
  {
    label: 'Articles',
    icon: <BookOpen className="w-4 h-4" />,
    href: '/ressources/articles',
    children: [
      { label: 'Tous les articles', href: '/ressources/articles' },
      { label: 'Nouvel article', href: '/ressources/articles/new/edit' },
    ],
  },
];

function NavLink({
  item,
  collapsed,
}: {
  item: NavItem;
  collapsed: boolean;
}) {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isActive =
    location.pathname === item.href ||
    (item.href !== '/' && location.pathname.startsWith(item.href));

  if (item.children && !collapsed) {
    return (
      <div>
        <button
          onClick={() => setOpen((v) => !v)}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            isActive
              ? 'bg-pink-50 text-pink-700'
              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
          }`}
        >
          <span className={isActive ? 'text-pink-600' : 'text-slate-400'}>
            {item.icon}
          </span>
          <span className="flex-1 text-left">{item.label}</span>
          <ChevronRight
            className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-90' : ''} ${isActive ? 'text-pink-500' : 'text-slate-300'}`}
          />
        </button>
        {open && (
          <div className="mt-1 ml-7 space-y-1">
            {item.children.map((child) => {
              const childActive = location.pathname === child.href;
              return (
                <Link
                  key={child.href}
                  to={child.href}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    childActive
                      ? 'bg-pink-50 text-pink-700'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                  }`}
                >
                  <PenSquare className="w-3 h-3" />
                  {child.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.href}
      title={collapsed ? item.label : undefined}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        isActive
          ? 'bg-pink-50 text-pink-700'
          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
      } ${collapsed ? 'justify-center' : ''}`}
    >
      <span className={isActive ? 'text-pink-600' : 'text-slate-400'}>
        {item.icon}
      </span>
      {!collapsed && <span>{item.label}</span>}
    </Link>
  );
}

export default function LayoutDashboard({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!user) {
    navigate('/');
    return null;
  }

  const sidebar = (
    <aside
      className={`${
        collapsed ? 'w-16' : 'w-56'
      } flex-shrink-0 bg-white border-r border-slate-200 flex flex-col transition-all duration-200`}
    >
      {/* Logo + collapse toggle */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-slate-100">
        {!collapsed && (
          <img
            src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-02.png"
            alt="Recruteurs.io"
            className="h-7 cursor-pointer"
            onClick={() => navigate('/')}
          />
        )}
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors ml-auto"
          title={collapsed ? 'Développer' : 'Réduire'}
        >
          <Menu className="w-4 h-4" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
        {NAV_ITEMS.map((item) => (
          <NavLink key={item.href} item={item} collapsed={collapsed} />
        ))}
      </nav>

      {/* User + logout */}
      <div className="border-t border-slate-100 px-2 py-3 space-y-1">
        {!collapsed && (
          <div className="px-3 py-1.5">
            <p className="text-xs font-medium text-slate-900 truncate">{user.email}</p>
          </div>
        )}
        <button
          onClick={logout}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Déconnexion' : undefined}
        >
          <LogOut className="w-4 h-4 shrink-0" />
          {!collapsed && <span>Déconnexion</span>}
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden md:flex">{sidebar}</div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div
            className="fixed inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative z-50 w-56 bg-white flex flex-col h-full shadow-xl">
            <div className="flex items-center justify-between px-4 py-4 border-b border-slate-100">
              <img
                src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-02.png"
                alt="Recruteurs.io"
                className="h-7"
              />
              <button
                onClick={() => setMobileOpen(false)}
                className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
              {NAV_ITEMS.map((item) => (
                <NavLink key={item.href} item={item} collapsed={false} />
              ))}
            </nav>
            <div className="border-t border-slate-100 px-2 py-3">
              <button
                onClick={logout}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile topbar */}
        <div className="md:hidden flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"
          >
            <Menu className="w-5 h-5" />
          </button>
          <img
            src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-02.png"
            alt="Recruteurs.io"
            className="h-6"
          />
        </div>

        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
